import React, { useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Player } from '../types';
import { formatPlayerName } from '../utils/playerUtils';
import './PlayerStatsModal.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface PlayerStatsModalProps {
  player: Player | null;
  isOpen: boolean;
  onClose: () => void;
}

const PlayerStatsModal: React.FC<PlayerStatsModalProps> = ({ player, isOpen, onClose }) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    // Prevent body scroll when modal is open
    const originalStyle = window.getComputedStyle(document.body).overflow;
    const originalPosition = window.getComputedStyle(document.body).position;
    const originalTop = window.getComputedStyle(document.body).top;
    
    // Store current scroll position
    const scrollY = window.scrollY;
    
    // Apply styles to prevent scrolling
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    // Function to ensure modal stays visible
    const ensureModalVisible = () => {
      if (modalRef.current) {
        const modal = modalRef.current;
        const rect = modal.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        
        // Check if modal is partially hidden by browser UI
        if (rect.top < 0) {
          modal.style.marginTop = `${Math.abs(rect.top) + 20}px`;
        }
        
        // Ensure modal doesn't exceed viewport height on mobile
        if (window.innerWidth <= 768) {
          const maxHeight = viewportHeight - 160; // Leave space for browser UI
          modal.style.maxHeight = `${maxHeight}px`;
          
          // Adjust modal body height
          const modalBody = modal.querySelector('.modal-body') as HTMLElement;
          if (modalBody) {
            const headerHeight = modal.querySelector('.modal-header')?.clientHeight || 0;
            modalBody.style.maxHeight = `${maxHeight - headerHeight - 32}px`;
          }
        }
      }
    };

    // Prevent scroll events from bubbling through modal
    const preventScroll = (e: Event) => {
      // Only prevent if the event is not coming from within the modal
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // Listen for viewport changes (orientation, fullscreen, etc.)
    const handleViewportChange = () => {
      // Small delay to allow browser UI to settle
      setTimeout(ensureModalVisible, 100);
      
      // Additional delay for mobile browsers
      if (window.innerWidth <= 768) {
        setTimeout(ensureModalVisible, 300);
      }
    };

    // Add event listeners
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('orientationchange', handleViewportChange);
    document.addEventListener('visibilitychange', handleViewportChange);
    
    // Prevent scroll events on document
    document.addEventListener('wheel', preventScroll, { passive: false });
    document.addEventListener('touchmove', preventScroll, { passive: false });
    document.addEventListener('keydown', (e) => {
      // Prevent arrow keys, page up/down, home, end from scrolling
      if ([32, 33, 34, 35, 36, 37, 38, 39, 40].includes(e.keyCode)) {
        e.preventDefault();
      }
    });
    
    // Check visibility on mount
    setTimeout(ensureModalVisible, 100);

    // Cleanup
    return () => {
      // Restore body styles and scroll position
      document.body.style.overflow = originalStyle;
      document.body.style.position = originalPosition;
      document.body.style.top = originalTop;
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
      
      // Remove event listeners
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
      document.removeEventListener('visibilitychange', handleViewportChange);
      document.removeEventListener('wheel', preventScroll);
      document.removeEventListener('touchmove', preventScroll);
    };
  }, [isOpen]);

  if (!isOpen || !player) return null;

  // Prepare data for the chart
  const roundNumbers = Object.keys(player.roundStars || {})
    .map(Number)
    .sort((a, b) => a - b);

  const starsData = roundNumbers.map(round => player.roundStars[round]);

  const chartData = {
    labels: roundNumbers.map(round => `Kierros ${round}`),
    datasets: [
      {
        label: 'Tähdet',
        data: starsData,
        backgroundColor: 'rgba(254, 204, 39, 0.8)',
        borderColor: 'rgba(254, 204, 39, 1)',
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: `${formatPlayerName(player)} - Tähdet kierroksittain`,
        font: {
          size: 16,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Tähdet',
        },
      },
      x: {
        title: {
          display: true,
          text: 'Kierros',
        },
      },
    },
  };

  // Calculate some statistics
  const totalRounds = roundNumbers.length;
  const averageStars = totalRounds > 0 ? (player.totalStars / totalRounds).toFixed(1) : 0;
  const maxStarsInRound = Math.max(...starsData);
  const roundsWithStars = starsData.filter(stars => stars > 0).length;

  return (
    <div 
      className="modal-overlay" 
      onClick={onClose}
      onWheel={(e) => e.preventDefault()}
      onTouchMove={(e) => e.preventDefault()}
    >
      <div ref={modalRef} className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{formatPlayerName(player)}</h2>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-body">
          <div className="player-summary">
            <div className="summary-item">
              <span className="label">Sijoitus:</span>
              <span className="value">#{player.currentRank}</span>
            </div>
            <div className="summary-item">
              <span className="label">Yhteensä tähtiä:</span>
              <span className="value">{player.totalStars}</span>
            </div>
            <div className="summary-item">
              <span className="label">Keskiarvo kierroksella:</span>
              <span className="value">{averageStars}</span>
            </div>
            <div className="summary-item">
              <span className="label">Parhaan kierroksen tähdet:</span>
              <span className="value">{maxStarsInRound}</span>
            </div>
            <div className="summary-item">
              <span className="label">Tähtikierroksia:</span>
              <span className="value">{roundsWithStars}</span>
            </div>
          </div>

          <div className="chart-container">
            <Bar data={chartData} options={chartOptions} />
          </div>

          <div className="round-details">
            <h3>Yksityiskohdat kierroksittain:</h3>
            <div className="rounds-grid">
              {roundNumbers.map(round => (
                <div key={round} className="round-item">
                  <span className="round-number">Kierros {round}</span>
                  <span className="round-stars">{player.roundStars[round]} ⭐</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerStatsModal;
